import { useEffect, useState } from 'react';
import financasApi from '@/lib/financasApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Pencil, Trash2, Tag } from 'lucide-react';
import { toast } from 'sonner';

const CORES = ['#22c55e', '#3b82f6', '#ef4444', '#f59e0b', '#8b5cf6', '#ec4899', '#14b8a6', '#06b6d4', '#a855f7', '#f97316'];

export default function FinancasCategorias() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nome: '', tipo: 'entrada', cor: '#22c55e' });

  const load = async () => {
    const { data } = await financasApi.get('/financas/categorias');
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const openNovo = () => { setEditing(null); setForm({ nome: '', tipo: 'entrada', cor: '#22c55e' }); setOpen(true); };
  const openEditar = (it) => { setEditing(it); setForm({ nome: it.nome, tipo: it.tipo, cor: it.cor || '#3b82f6' }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim()) { toast.error('Nome obrigatório'); return; }
    try {
      if (editing) {
        await financasApi.put(`/financas/categorias/${editing.id}`, form);
        toast.success('Atualizada');
      } else {
        await financasApi.post('/financas/categorias', form);
        toast.success('Criada');
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro');
    }
  };

  const excluir = async (it) => {
    if (!window.confirm(`Excluir "${it.nome}"? As transações desta categoria não serão removidas.`)) return;
    await financasApi.delete(`/financas/categorias/${it.id}`);
    toast.success('Excluída');
    load();
  };

  const groupBy = (t) => items.filter(i => i.tipo === t || (t === 'todas' && i.tipo === 'ambos'));

  return (
    <div className="p-4 md:p-8 max-w-[1400px] mx-auto" data-testid="financas-categorias-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3"><Tag className="w-6 h-6 md:w-7 md:h-7 text-emerald-400" /> Categorias</h1>
          <p className="text-slate-400 text-xs md:text-sm mt-1">Organize suas entradas e saídas</p>
        </div>
        <Button onClick={openNovo} data-testid="financas-categoria-novo-btn" className="bg-emerald-600 hover:bg-emerald-500 text-white">
          <Plus className="w-4 h-4 mr-2" /> Nova Categoria
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {[
          { key: 'entrada', label: 'Entradas', cor: 'emerald' },
          { key: 'saida', label: 'Saídas', cor: 'red' },
        ].map(({ key, label, cor }) => (
          <div key={key} className="bg-slate-900 border border-white/10 rounded-xl p-5">
            <h3 className={`text-${cor}-400 font-semibold mb-4 uppercase tracking-wider text-xs`}>{label}</h3>
            <div className="space-y-2">
              {groupBy(key).length === 0 && <p className="text-slate-500 text-sm">Nenhuma categoria</p>}
              {groupBy(key).map(c => (
                <div key={c.id} className="flex items-center justify-between bg-slate-950 border border-white/5 rounded-lg p-3">
                  <div className="flex items-center gap-3">
                    <span className="w-3 h-3 rounded-full" style={{ background: c.cor }} />
                    <span className="text-white text-sm">{c.nome}</span>
                    {c.sistema && <span className="text-[10px] uppercase text-slate-500 bg-white/5 px-2 py-0.5 rounded">padrão</span>}
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => openEditar(c)} className="text-slate-400 hover:text-white" data-testid={`financas-categoria-editar-${c.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => excluir(c)} className="text-red-500 hover:text-red-400" data-testid={`financas-categoria-excluir-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar' : 'Nova'} Categoria</DialogTitle>
            <DialogDescription className="text-slate-400">Defina nome, tipo e cor.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-slate-400 text-xs">Nome *</Label>
              <Input data-testid="financas-categoria-form-nome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} className="bg-slate-950 border-white/10 text-white" required />
            </div>
            <div>
              <Label className="text-slate-400 text-xs">Tipo</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                <SelectTrigger className="bg-slate-950 border-white/10 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-white/10 text-white">
                  <SelectItem value="entrada">Entrada</SelectItem>
                  <SelectItem value="saida">Saída</SelectItem>
                  <SelectItem value="ambos">Ambos</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-400 text-xs">Cor</Label>
              <div className="flex gap-2 flex-wrap mt-1">
                {CORES.map(c => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setForm({ ...form, cor: c })}
                    className={`w-8 h-8 rounded-full border-2 ${form.cor === c ? 'border-white' : 'border-transparent'}`}
                    style={{ background: c }}
                  />
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)} className="text-slate-400">Cancelar</Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="financas-categoria-form-salvar">{editing ? 'Atualizar' : 'Criar'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
